<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed, onBeforeMount, ref } from 'vue';

  import type { UserListItem } from '@stuut/api-sdk/models';
  import type { User } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import AssigneeSelect from '@/components/users/AssigneeSelect.vue';
  import { getApiErrorMessage } from '@/utils/apiErrors';

  interface Props {
    user: Pick<UserListItem, 'id' | 'name'>;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    deactivated: [];
  }>();

  const isOpen = defineModel<boolean>('open', { default: false });

  const isLoading = ref(false);
  const isUpdating = ref(false);

  const reassignTo = ref<User | null>(null);

  const numAssignedPartners = ref(0);
  const numAssignedTasks = ref(0);

  const confirm = () => {
    if (isUpdating.value) return;
    isUpdating.value = true;

    stuut.users
      .deactivate(props.user.id, reassignTo.value?.id)
      .then(() => {
        emit('deactivated');
        toast.success(`${props.user.name} has been deactivated.`);
        isOpen.value = false;
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Could not deactivate user'));
      })
      .finally(() => {
        isUpdating.value = false;
      });
  };

  onBeforeMount(async () => {
    isLoading.value = true;
    try {
      const stats = await stuut.users.getAssignmentStats(props.user.id);
      numAssignedPartners.value = stats.num_assigned_partners;
      numAssignedTasks.value = stats.num_assigned_tasks;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not load assignment details'));
    } finally {
      isLoading.value = false;
    }
  });

  interface AssignmentPart {
    count: number;
    label: string;
  }

  const assignmentParts = computed<AssignmentPart[]>(() => {
    const parts: AssignmentPart[] = [];
    if (numAssignedPartners.value) {
      parts.push({
        count: numAssignedPartners.value,
        label: pluralize('customer', numAssignedPartners.value),
      });
    }
    if (numAssignedTasks.value) {
      parts.push({
        count: numAssignedTasks.value,
        label: pluralize('task', numAssignedTasks.value),
      });
    }
    return parts;
  });
</script>

<template>
  <AlertDialog v-model:open="isOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Deactivate User </AlertDialogTitle>
      </AlertDialogHeader>

      <AlertDialogDescription class="space-y-6 text-base text-foreground">
        <template v-if="isLoading">
          <div class="flex w-full h-full items-center justify-center">
            <LoadingSpinner class="text-lg far" />
          </div>
        </template>

        <template v-else-if="assignmentParts.length">
          <p>
            {{ user.name }} has
            <template v-for="(part, idx) in assignmentParts" :key="part.label">
              <strong>{{ part.count }} {{ part.label }}</strong>
              <template v-if="idx < assignmentParts.length - 1"> and </template>
            </template>
            assigned to them.
          </p>
          <div class="flex items-center gap-2">
            <p>Reassign to:</p>
            <AssigneeSelect :assignee="reassignTo" @selected="(u) => (reassignTo = u)" />
          </div>
        </template>

        <p v-else>Are you sure you want to deactivate {{ user.name }}?</p>
      </AlertDialogDescription>

      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <Button :disabled="isLoading" :loading="isUpdating" @click="confirm()">Confirm</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped></style>
